import { RuntimeConfig } from '@occc/config';
import {
  DateTimeFormatTZ,
  formatDateTimeTZ,
  getUserTimezone,
  isDateTimeValid,
  parseDate,
} from '@occc-app/lib/date';
import { CommunicationPreferenceFragment } from '@occc/graphql';

import { CommunicationPreferenceMapItem } from './communication-preferences.types';

export const getSubBrandName = (config: RuntimeConfig, subBrandUuid?: string | null) => {
  // If subBrandUuid is null or undefined, it means these communication preferences
  // are for the main brand and we should return the main brand name from config.
  if (!subBrandUuid) {
    return config.brandName;
  }

  const subBrand = config.subBrands?.list.find((item) => item.uuid === subBrandUuid);

  return subBrand?.name ?? '';
};

const formatPreferenceDateTime = (dateTime?: string | null) => {
  if (!dateTime) {
    return null;
  }

  const parsedDate = parseDate(dateTime);

  if (!isDateTimeValid(parsedDate)) {
    return null;
  }

  return formatDateTimeTZ(
    parsedDate,
    getUserTimezone(),
    DateTimeFormatTZ.DateTimeSlashed12hWithShortTimeZone,
  );
};

export const getOptedInOutText = (item: CommunicationPreferenceMapItem) => {
  let itemText = !item.optInAt || !item.optOutAt ? 'No opt-in set' : '';
  const formattedOptInAt = formatPreferenceDateTime(item.optInAt);
  const formattedOptOutAt = formatPreferenceDateTime(item.optOutAt);

  if (item.optIn && formattedOptInAt) {
    itemText = `Opted in at: ${formattedOptInAt}`;
  }

  if (!item.optIn && formattedOptOutAt) {
    itemText = `Opted out at: ${formattedOptOutAt}`;
  }

  return itemText;
};

export const getCommunicationPreferencesMap = (
  {
    smsOptIn,
    smsOptInAt,
    smsOptOutAt,
    emailOptIn,
    emailOptInAt,
    emailOptOutAt,
    pushOptIn,
    pushOptInAt,
    pushOptOutAt,
  }: CommunicationPreferenceFragment,
  config: RuntimeConfig,
): CommunicationPreferenceMapItem[] => [
  {
    title: 'SMS Marketing Messages',
    name: 'smsOpt',
    optIn: smsOptIn,
    optInAt: smsOptInAt,
    optOutAt: smsOptOutAt,
    isEnabled: config.communicationPreference.optInConfigurable.sms,
  },
  {
    title: 'Email Marketing Messages',
    name: 'emailOpt',
    optIn: emailOptIn,
    optInAt: emailOptInAt,
    optOutAt: emailOptOutAt,
    isEnabled: config.communicationPreference.optInConfigurable.email,
  },
  {
    title: 'Push Notifications',
    name: 'pushOpt',
    optIn: pushOptIn,
    optInAt: pushOptInAt,
    optOutAt: pushOptOutAt,
    isEnabled: config.communicationPreference.optInConfigurable.push,
  },
];
